import React, { useEffect, useRef, useState } from 'react';
import { RotateCw } from 'lucide-react';
import { canvasGestureTransform } from '../../lib/creatorCanvas';

export default function CreatorTransformOverlay({ transform, width, height, size, locked, time, onDraft, onCommit, onStart }) {
  const gesture = useRef(null);
  const frame = useRef(null);
  const [guides, setGuides] = useState({});
  useEffect(() => {
    const escape = event => {
      if (event.key !== 'Escape' || !gesture.current) return;
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null; gesture.current = null; setGuides({}); onDraft(null);
    };
    window.addEventListener('keydown', escape);
    return () => { window.removeEventListener('keydown', escape); if (frame.current !== null) cancelAnimationFrame(frame.current); };
  }, [onDraft]);
  const start = (event, mode) => {
    if (locked || event.button !== 0) return;
    event.preventDefault(); event.stopPropagation(); onStart?.();
    const rect = event.currentTarget.closest('.creator-stage').getBoundingClientRect();
    gesture.current = { mode, initial: transform, time, x: event.clientX, y: event.clientY,
      centerX: rect.left + transform.x * width, centerY: rect.top + transform.y * height,
      point: { x: event.clientX, y: event.clientY, shift: event.shiftKey }, next: transform };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const flush = () => {
    const active = gesture.current;
    if (!active) return;
    const result = canvasGestureTransform(active.initial, active, active.point, { width, height });
    active.next = result.transform; setGuides(result.guides); onDraft(result.transform);
  };
  const move = event => {
    if (!gesture.current) return;
    gesture.current.point = { x: event.clientX, y: event.clientY, shift: event.shiftKey };
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => { frame.current = null; flush(); });
  };
  const finish = (event, cancel = false) => {
    const active = gesture.current;
    if (!active) return;
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    if (!cancel) { active.point = { x: event.clientX, y: event.clientY, shift: event.shiftKey }; flush(); }
    gesture.current = null; setGuides({}); onDraft(null);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (!cancel && ['x', 'y', 'scale', 'rotation'].some(property => Math.abs(active.next[property] - active.initial[property]) > 1e-6)) onCommit(active.next, active.time);
  };
  const events = { onPointerMove: move, onPointerUp: event => finish(event), onPointerCancel: event => finish(event, true), onLostPointerCapture: event => finish(event, true) };
  return <>
    {guides.x !== undefined && <i className="creator-canvas-guide creator-canvas-guide-x" style={{ left: `${guides.x * 100}%` }} />}
    {guides.y !== undefined && <i className="creator-canvas-guide creator-canvas-guide-y" style={{ top: `${guides.y * 100}%` }} />}
    <div className={`creator-transform-box ${locked ? 'is-locked' : ''}`} role="button" tabIndex={0} aria-label={locked ? 'Capa bloqueada' : 'Mover capa visual'}
      style={{ left: `${transform.x * 100}%`, top: `${transform.y * 100}%`, width: Math.max(8, size.width * transform.scale), height: Math.max(8, size.height * transform.scale), transform: `translate(-50%,-50%) rotate(${transform.rotation}deg)` }}
      onPointerDown={event => start(event, 'position')} {...events} onKeyDown={event => {
        if (locked || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
        event.preventDefault(); event.stopPropagation();
        const amount = event.shiftKey ? 10 : 1;
        onCommit({ ...transform, x: Math.max(-2, Math.min(3, transform.x + (event.key === 'ArrowLeft' ? -amount : event.key === 'ArrowRight' ? amount : 0) / width)),
          y: Math.max(-2, Math.min(3, transform.y + (event.key === 'ArrowUp' ? -amount : event.key === 'ArrowDown' ? amount : 0) / height)) }, time);
      }}>
      {!locked && <>{['nw', 'ne', 'sw', 'se'].map(corner => <button key={corner} type="button" className={`creator-transform-handle ${corner}`} aria-label={`Escalar capa ${corner}`} title="Escalar capa" onPointerDown={event => start(event, 'scale')} {...events} />)}
        <button type="button" className="creator-rotation-handle" aria-label="Rotar capa visual" title="Rotar capa" onPointerDown={event => start(event, 'rotation')} {...events}><RotateCw size={12} /></button></>}
    </div>
  </>;
}
